import { useCallback, useState } from 'react';

/**
 * Tab validation state record.
 * Keys are tab IDs, values are boolean validity flags.
 */
export type TabValidationState = Record<string, boolean>;

/**
 * Return type for the useTabValidation hook.
 */
export interface UseTabValidationReturn {
  /** Current validation state for all tabs */
  tabValid: TabValidationState;
  /** Callback to update a specific tab's validation state */
  handleSetTabValid: (tab: string, isValid: boolean) => void;
}

/**
 * Custom hook for managing tab validation state across feature detail pages.
 *
 * Provides a centralized way to track whether each tab in a multi-tab form
 * contains valid data. The validation state can be updated by individual tabs
 * and is used to display error indicators on tab headers.
 *
 * @param tabNames - Array of tab IDs to initialize with valid state
 * @returns Object containing tabValid state and handleSetTabValid callback
 *
 * @example
 * ```typescript
 * const { tabValid, handleSetTabValid } = useTabValidation(['info', 'billing', 'integrations']);
 *
 * // In tab configuration
 * {
 *   id: 'info',
 *   hasError: !tabValid.info,
 *   // ...
 * }
 *
 * // Pass to FeatureDetailsTemplate
 * <FeatureDetailsTemplate
 *   setTabValid={handleSetTabValid}
 *   // ...
 * />
 * ```
 */
export function useTabValidation(tabNames: string[]): UseTabValidationReturn {
  // Initialize all tabs as valid
  const initialState = tabNames.reduce((acc, tabName) => {
    acc[tabName] = true;
    return acc;
  }, {} as TabValidationState);

  const [tabValid, setTabValid] = useState<TabValidationState>(initialState);

  const handleSetTabValid = useCallback((tab: string, isValid: boolean) => {
    setTabValid(prev => ({
      ...prev,
      [tab]: isValid,
    }));
  }, []);

  return {
    tabValid,
    handleSetTabValid,
  };
}
